import ReactGA from "react-ga4";

import { ActionType, EventName, InteractionType, Source, ViewType } from "@/constants/analytics";
import { InteractionMethod } from "@/constants/swipeConstants";

const GA_MEASUREMENT_ID = "G-VRRLW986S9";

if (!import.meta.env.VITE_APP_ENV) {
  console.error(
    "VITE_APP_ENV is not set; GA4 events will be tagged env='dev'. Check build configuration."
  );
}
const APP_ENV = import.meta.env.VITE_APP_ENV || "dev";

interface CommonEventParams {
  refloop_user_id: string;
  env: string;
  source: Source;
  timestamp: string;
}

const buildCommonParams = (userId: string, source: Source = Source.web): CommonEventParams => ({
  refloop_user_id: userId,
  env: APP_ENV,
  source,
  timestamp: new Date().toISOString()
});

const stripUndefined = <T extends Record<string, unknown>>(obj: T): Partial<T> => {
  const out: Partial<T> = {};
  (Object.keys(obj) as (keyof T)[]).forEach((key) => {
    const v = obj[key];
    if (v !== undefined) {
      out[key] = v;
    }
  });
  return out;
};

const sendEvent = (name: EventName, params: Record<string, unknown>): void => {
  try {
    const clean = stripUndefined(params);
    ReactGA.event(name, clean);
    if (import.meta.env.DEV) {
      console.log("GA Event sent:", name, clean);
    }
  } catch (error) {
    console.error("Failed to send GA event:", error);
  }
};

export const initGA = (debug = false) => {
  try {
    ReactGA.initialize(GA_MEASUREMENT_ID, {
      gaOptions: {
        debug_mode: debug
      },
      gtagOptions: {
        transport_type: "beacon"
      }
    });
    console.log("Google Analytics initialized with react-ga4");
  } catch (error) {
    console.error("Failed to initialize Google Analytics:", error);
  }
};

export interface SpotInteractionParams {
  user_id: string;
  spot_id: string;
  spot_name?: string;
  recommendation_id: string;
  interaction_type: InteractionType;
  action_type: ActionType;
  interaction_method: InteractionMethod;
  user_latitude: number | null;
  user_longitude: number | null;
  spot_distance: number | null;
  card_position?: number | null;
  total_recommendations?: number | null;
}

export const trackSpotInteraction = (params: SpotInteractionParams) => {
  if (!params.user_id) {
    throw new Error("Cannot send GA event: user_id is required");
  }
  sendEvent(EventName.spot_interaction, {
    ...buildCommonParams(params.user_id),
    spot_id: params.spot_id,
    spot_name: params.spot_name,
    recommendation_id: params.recommendation_id,
    interaction_type: params.interaction_type,
    action_type: params.action_type,
    interaction_method: params.interaction_method,
    user_latitude: params.user_latitude,
    user_longitude: params.user_longitude,
    spot_distance: params.spot_distance,
    card_position: params.card_position,
    total_recommendations: params.total_recommendations
  });
};

export interface ViewSpotDetailParams {
  user_id: string;
  spot_id: string;
  spot_name?: string;
  /** 推薦コンテキストから到達した場合のみ設定。保存/訪問リストからのタップでは undefined */
  recommendation_id?: string;
  view_type: ViewType;
  card_position?: number | null;
}

export const trackViewSpotDetail = (params: ViewSpotDetailParams) => {
  if (!params.user_id) {
    console.warn("Skip view_spot_detail: user_id is empty");
    return;
  }
  sendEvent(EventName.view_spot_detail, {
    ...buildCommonParams(params.user_id),
    spot_id: params.spot_id,
    spot_name: params.spot_name,
    recommendation_id: params.recommendation_id,
    view_type: params.view_type,
    card_position: params.card_position
  });
};

export interface RequestRecommendationParams {
  user_id: string;
  recommendation_id: string;
  mood_category: string | null;
  genre: string | null;
  subgenre?: string | null;
  result_count?: number | null;
  /** 推薦のイテレーション (1=初回、2,3,...=load more) */
  recommendation_iter?: number;
}

export const trackRequestRecommendation = (params: RequestRecommendationParams) => {
  if (!params.user_id) {
    console.warn("Skip request_recommendation: user_id is empty");
    return;
  }
  sendEvent(EventName.request_recommendation, {
    ...buildCommonParams(params.user_id),
    recommendation_id: params.recommendation_id,
    mood_category: params.mood_category,
    genre: params.genre,
    subgenre: params.subgenre,
    result_count: params.result_count,
    recommendation_iter: params.recommendation_iter
  });
};

export interface ToggleLineDeliveryParams {
  user_id: string;
  enabled: boolean;
  day_of_week: string;
  time: string;
}

export const trackToggleLineDelivery = (params: ToggleLineDeliveryParams) => {
  if (!params.user_id) {
    console.warn("Skip toggle_line_delivery: user_id is empty");
    return;
  }
  sendEvent(EventName.toggle_line_delivery, {
    ...buildCommonParams(params.user_id),
    enabled: params.enabled,
    day_of_week: params.day_of_week,
    time: params.time
  });
};

export const trackPageView = (path: string) => {
  try {
    ReactGA.send({
      hitType: "pageview",
      page: path,
      title: document.title,
      env: APP_ENV
    });
    if (import.meta.env.DEV) {
      console.log("GA PageView sent:", path);
    }
  } catch (error) {
    console.error("Failed to send GA pageview:", error);
  }
};

export interface UserProperties {
  user_id?: string;
  user_status?: string;
  line_delivery_enabled?: boolean;
  line_delivery_day_of_week?: string;
  weekend_delivery_enabled?: boolean;
  /** GA4 user property を削除するには `null` を渡す (旧版 PII 削除等に使う) */
  [key: string]: string | number | boolean | undefined | null;
}

export const setUserProperties = (properties: UserProperties) => {
  try {
    setTimeout(() => {
      ReactGA.gtag("set", "user_properties", properties);
      if (import.meta.env.DEV) {
        console.log("GA User Properties set:", properties);
      }
    }, 0);
  } catch (error) {
    console.error("Failed to set GA user properties:", error);
  }
};

export const setUserId = (userId: string) => {
  try {
    ReactGA.gtag("config", GA_MEASUREMENT_ID, {
      user_id: userId
    });
    if (import.meta.env.DEV) {
      console.log("GA User ID set:", userId);
    }
  } catch (error) {
    console.error("Failed to set GA user ID:", error);
  }
};
